import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export default function Header({ onCta }) {
  const [open, setOpen] = useState(false);
  const links = [
    { to: "leistungen", label: "Leistungen" },
    { to: "ablauf", label: "So funktioniert's" },
    { to: "einsatzgebiet", label: "Einsatzgebiet" },
    { to: "kontakt", label: "Kontakt" },
  ];

  // Robust smooth-scroll to sections that also works after closing the mobile menu
  const scrollToSection = (id) => {
    const doScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerOffset = 112; // top bar (36) + main nav (96) + gap
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    };
    // Wait for the mobile menu to close so layout is stable
    if (open) {
      setOpen(false);
      setTimeout(doScroll, 60);
    } else {
      doScroll();
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]">
      {/* Top contact bar */}
      <div className="bg-[#2C3E2D] text-[#E8ECE1] text-[12px]">
        <div className="max-w-6xl mx-auto px-6 h-9 flex items-center justify-between gap-4">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="header-top-phone"
            className="inline-flex items-center gap-2 hover:text-white transition-colors font-medium"
          >
            <Phone className="w-3.5 h-3.5 text-[#95A97A]" strokeWidth={2} />
            {COMPANY.phone}
          </a>
          <a
            href={`mailto:${COMPANY.email}`}
            data-testid="header-top-email"
            className="hidden sm:inline-flex items-center gap-2 hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5 text-[#95A97A]" strokeWidth={2} />
            {COMPANY.email}
          </a>
          <div className="hidden md:block text-[#95A97A]">Alles rund ums Haus – Rundum-Sorglos-Paket</div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-6xl mx-auto px-6 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img
            src={LOGO_URL}
            alt="Blessing Dienstleistungen"
            className="h-20 w-auto"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-[17px] font-semibold text-[#1A201A]">{COMPANY.name}</div>
            <div className="text-[12px] text-[#6B7F3E] font-medium">Objektpflege</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              type="button"
              onClick={() => scrollToSection(l.to)}
              className="text-sm text-[#2C3E2D] hover:text-[#6B7F3E] transition-colors font-medium"
              data-testid={`header-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="header-cta-btn"
            onClick={onCta}
            className="rounded-full bg-[#6B7F3E] hover:bg-[#5A6B34] text-white px-5"
          >
            Termin sichern
          </Button>
        </nav>
        <button
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={open}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white/95">
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.label}
                type="button"
                onClick={() => scrollToSection(l.to)}
                className="text-[15px] text-[#2C3E2D] py-1 text-left"
                data-testid={`header-mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => { setOpen(false); onCta(); }}
              className="rounded-full bg-[#6B7F3E] hover:bg-[#5A6B34] text-white w-full mt-2"
              data-testid="header-mobile-cta-btn"
            >
              Termin sichern
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
